import { useState } from 'react';
import { Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SupportDialog } from '@/components/SupportDialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import type { NostrEvent } from '@nostrify/nostrify';
import { cn } from '@/lib/utils';

interface SupportButtonProps {
  pubkey: string;
  event: NostrEvent;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function SupportButton({ 
  pubkey, 
  event, 
  className,
  variant = 'outline',
  size = 'icon'
}: SupportButtonProps) {
  const [open, setOpen] = useState(false);
  const { user } = useCurrentUser();
  const { data: authorData } = useAuthor(pubkey);

  const metadata = authorData?.metadata;
  const hasLightningAddress = !!(metadata?.lud06 || metadata?.lud16);
  
  // Don't show button if:
  // - User is not logged in
  // - User is the author
  // - Author has no lightning address
  const isDisabled = !user || user.pubkey === pubkey || !hasLightningAddress;
  const isHidden = user?.pubkey === pubkey; // Hide completely if user is the author

  if (isHidden) {
    return null;
  }

  const button = (
    <Button
      variant={variant}
      size={size}
      className={cn(
        'rounded-xl border-border/60',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !isDisabled && setOpen(true)}
      disabled={isDisabled}
    >
      <Zap className="h-4 w-4" />
    </Button>
  );

  // Show tooltip for disabled states
  if (isDisabled) {
    let tooltipMessage = 'Support this creator';
    if (!user) {
      tooltipMessage = 'Log in to send a zap';
    } else if (!hasLightningAddress) {
      tooltipMessage = 'Creator has no lightning address';
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>Support this creator</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <SupportDialog
        pubkey={pubkey}
        event={event}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
